import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { type CountryCode } from 'libphonenumber-js';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { TenantOnboardingApiService } from 'src/app/core/services/tenant-onboarding-api.service';
import type { IssuedApiKey } from 'src/app/core/services/tenant-onboarding-api.service';
import { RulesApiService } from 'src/app/core/services/rules-api.service';
import { UiAlertService } from 'src/app/core/services/ui-alert.service';
import { IntlPhoneFieldComponent } from 'src/app/shared/components/intl-phone-field/intl-phone-field.component';
import {
  dialStringForCountry,
  normalizeNationalDigits,
  validateNationalPhone,
} from 'src/app/shared/utils/international-phone';
import {
  normalizeGstin,
  normalizePan,
  validateIndianGstin,
  validateIndianPan,
} from 'src/app/shared/utils/india-tax-ids';
import { slugifyTenantSubdomain } from 'src/app/shared/utils/string.util';
import {
  buildCommercialMetadataJson,
  emptySelectionMap,
  hasAnyProductSelected,
  parseCatalogFromDropdownEnvelope,
  type CatalogRow,
} from 'src/app/shared/utils/tenant-onboarding-catalog';
import {
  applyTenantFormApiErrorToForm,
} from 'src/app/shared/utils/tenant-form-http-error';

@Component({
  selector: 'app-create-client',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    IntlPhoneFieldComponent,
  ],
  templateUrl: './create-client.component.html',
  styleUrl: './create-client.component.css',
})
export class CreateClientComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly tenantApi = inject(TenantOnboardingApiService);
  private readonly rulesApi = inject(RulesApiService);
  private readonly uiAlert = inject(UiAlertService);

  @ViewChild('contactPhoneField') contactPhoneField?: IntlPhoneFieldComponent;
  @ViewChild('personPhoneField') personPhoneField?: IntlPhoneFieldComponent;

  /** Success modal after Save */
  showSuccessModal = false;
  private successRedirectTimer: ReturnType<typeof setTimeout> | null = null;

  /** Default API key issued during onboarding — shown once so the operator can copy it. */
  issuedApiKey: IssuedApiKey | null = null;
  apiKeyCopied = false;

  /** Tenant / directory */
  businessName = '';
  tenantSubdomain = '';
  contactCountryIso: CountryCode = 'IN';
  contactNational = '';
  contactPerson = '';
  contactPersonCountryIso: CountryCode = 'IN';
  contactPersonNational = '';
  email = '';
  /**
   * ISO 3166-1 alpha-2 of the country this tenant operates in. Drives the country pack,
   * currency, timezone and which tax identifiers are required — India's GSTIN/PAN were
   * previously mandatory for every tenant, which made a non-India tenant impossible to create.
   */
  countryIso2 = 'IN';

  /**
   * Countries a tenant can be created for, from the country-pack master.
   *
   * This was a hardcoded list, so onboarding a country stayed impossible until someone
   * remembered to edit it here too — USA was added to the packs and never appeared. The seeded
   * list stays as a fallback so the form still works if the master cannot be read.
   */
  countryOptions: { iso2: string; label: string }[] = [
    { iso2: 'IN', label: 'India' },
    { iso2: 'AE', label: 'United Arab Emirates' },
    { iso2: 'SG', label: 'Singapore' },
    { iso2: 'GB', label: 'United Kingdom' },
  ];

  /** UAE TRN, collected instead of GSTIN/PAN. */
  trn = '';

  /** A pack without an ISO country (CUSTOM) cannot be selected here, and disabled packs are out. */
  private loadCountryOptions(): void {
    this.rulesApi.listCountryPackRecords().subscribe({
      next: (res) => {
        const options = (res.data ?? [])
          .filter((pack) => !!pack.primary_iso2 && pack.status !== 'DISABLED')
          .map((pack) => ({ iso2: pack.primary_iso2 as string, label: pack.display_name }));
        if (options.length) this.countryOptions = options;
      },
      error: () => undefined,
    });
  }

  private taxIdentifiersPayload(): Record<string, string> {
    if (this.isIndiaTenant) {
      return { GSTN: normalizeGstin(this.gstn), PAN: normalizePan(this.businessPan) };
    }
    if (this.isUaeTenant) {
      return { TRN: this.trn.replace(/\s/g, '') };
    }
    return {};
  }

  get isIndiaTenant(): boolean {
    return this.countryIso2 === 'IN';
  }

  get isUaeTenant(): boolean {
    return this.countryIso2 === 'AE';
  }

  gstn = '';
  billingAddress = '';
  billingCity = '';
  billingPincode = '';
  businessPan = '';

  logoFile: File | null = null;
  logoFileName = '';
  profilePictureFile: File | null = null;
  profilePictureFileName = '';

  /** From `GET /product/types/dropdown` — product type slugs. */
  productsCatalog: CatalogRow[] = [];
  /** `[slug]: checked` — keys match catalog slugs. */
  productSelection: Record<string, boolean> = {};
  catalogLoading = true;
  catalogLoadError = '';

  amountReceived = '';
  dateOfReceipt: Date | string | null = '';
  unNumber = '';

  /** Per-PD charge amount per product slug (string from the input; only kept for selected products). */
  productCharges: Record<string, string> = {};

  /** PREPAID tenants buy subscription plans; POSTPAID tenants are billed per PD. */
  billingType: 'PREPAID' | 'POSTPAID' = 'POSTPAID';
  billingFrequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | '' = '';
  readonly billingFrequencies = [
    { value: 'DAILY', label: 'Daily' },
    { value: 'WEEKLY', label: 'Weekly' },
    { value: 'MONTHLY', label: 'Monthly' },
  ];

  get isPostpaid(): boolean {
    return this.billingType === 'POSTPAID';
  }

  /** Switching to prepaid clears postpaid-only inputs so stale rates are never submitted. */
  onBillingTypeChange(type: 'PREPAID' | 'POSTPAID'): void {
    this.billingType = type;
    if (type === 'PREPAID') {
      this.billingFrequency = '';
      this.productCharges = {};
    }
    this.clearSubmitErrorOnly();
  }

  submitting = false;
  submitError = '';

  /** Inline validation messages shown under each field (all populated on submit when invalid). */
  fieldErrors: Partial<
    Record<
      | 'businessName'
      | 'tenantSubdomain'
      | 'email'
      | 'contactPhone'
      | 'contactPersonPhone'
      | 'gstn'
      | 'trn'
      | 'businessPan'
      | 'panImage'
      | 'products'
      | 'dateOfReceipt',
      string
    >
  > = {};

  /** Clears submit banner and a field-level error when the user edits that field. */
  clearFieldFeedback(
    key:
      | 'businessName'
      | 'tenantSubdomain'
      | 'email'
      | 'contactPhone'
      | 'contactPersonPhone'
      | 'gstn'
      | 'trn'
      | 'businessPan'
      | 'panImage'
      | 'products'
      | 'dateOfReceipt',
  ): void {
    this.submitError = '';
    if (!this.fieldErrors[key]) {
      return;
    }
    const next = { ...this.fieldErrors };
    delete next[key];
    this.fieldErrors = next;
  }

  /** Phone components: clear contact / person phone errors when digits change. */
  onContactPhoneEdited(): void {
    this.clearFieldFeedback('contactPhone');
  }

  onPersonPhoneEdited(): void {
    this.clearFieldFeedback('contactPersonPhone');
  }

  /** Checkbox groups: clear group error once selection becomes valid. */
  onProductCheckboxChange(): void {
    this.submitError = '';
    if (this.hasAnyProductSelected()) {
      this.clearFieldFeedback('products');
    }
  }

  /** Clears API/submit banner when the user edits optional fields. */
  clearSubmitErrorOnly(): void {
    this.submitError = '';
  }

  readonly timezone = 'Asia/Kolkata';
  readonly currencyCode = 'INR';
  readonly currencySymbol = '₹';

  ngOnInit(): void {
    this.loadCountryOptions();
    this.tenantApi
      .listProductTypesDropdown()
      .pipe(finalize(() => (this.catalogLoading = false)))
      .subscribe({
        next: (productTypes) => {
          const productRows = parseCatalogFromDropdownEnvelope(productTypes);
          if (productRows === null) {
            this.catalogLoadError =
              productTypes?.message || 'Could not load product types.';
            return;
          }
          this.productsCatalog = productRows;
          this.productSelection = emptySelectionMap(this.productsCatalog);
        },
        error: () => {
          this.catalogLoadError =
            'Could not load product types. Check your session and API.';
        },
      });
  }

  trackBySlug(_index: number, row: CatalogRow): string {
    return row.slug;
  }

  suggestSubdomain(): void {
    const slug = slugifyTenantSubdomain(this.businessName || this.email.split('@')[0] || 'tenant');
    this.tenantSubdomain = slug || 'tenant-' + Math.random().toString(36).slice(2, 7);
  }

  /** On blur from business name: fill subdomain once if still empty */
  maybeSuggestSubdomainFromName(): void {
    if (this.businessName.trim() && !this.tenantSubdomain.trim()) {
      this.suggestSubdomain();
    }
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.logoFile = file ?? null;
    this.logoFileName = file ? file.name : '';
    this.submitError = '';
  }

  onProfilePictureSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.profilePictureFile = file ?? null;
    this.profilePictureFileName = file ? file.name : '';
    this.clearFieldFeedback('panImage');
  }

  blurNormalizeGst(): void {
    const t = normalizeGstin(this.gstn);
    if (t !== this.gstn) {
      this.gstn = t;
    }
  }

  blurNormalizePan(): void {
    const t = normalizePan(this.businessPan);
    if (t !== this.businessPan) {
      this.businessPan = t;
    }
  }

  private coercedReceiptDate(): Date | null {
    if (typeof this.dateOfReceipt === 'string') {
      return new Date(this.dateOfReceipt);
    }
    if (this.dateOfReceipt instanceof Date) {
      return this.dateOfReceipt;
    }
    return null;
  }

  /** Parsed commercial receipt date, or null if empty / invalid. */
  private parseDateOfReceiptValue(): Date | null {
    if (!this.dateOfReceipt) {
      return null;
    }
    const d = this.coercedReceiptDate();
    if (!d || Number.isNaN(d.getTime())) {
      return null;
    }
    return d;
  }

  /** Compares local calendar dates (ignores time-of-day). */
  private isCalendarDateBeforeToday(d: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const picked = new Date(d);
    picked.setHours(0, 0, 0, 0);
    return picked.getTime() < today.getTime();
  }

  private collectFieldValidationErrors(): typeof this.fieldErrors {
    const e: typeof this.fieldErrors = {};
    const name = this.businessName.trim();
    const sub = this.tenantSubdomain.trim().toLowerCase();
    const mail = this.email.trim().toLowerCase();

    if (!name) {
      e.businessName = 'Business name is required.';
    }
    this.applySubdomainValidationErrors(e, sub);
    this.applyEmailValidationErrors(e, mail);

    this.applySelectionValidationErrors(e);
    this.applyPhoneValidationErrors(e);
    this.applyTaxIdValidationErrors(e);

    const receipt = this.parseDateOfReceiptValue();
    if (receipt && this.isCalendarDateBeforeToday(receipt)) {
      e.dateOfReceipt = 'Date of receipt cannot be in the past.';
    }
    return e;
  }

  private applySelectionValidationErrors(e: typeof this.fieldErrors): void {
    if (!this.hasAnyProductSelected()) {
      e.products = 'Select at least one product type.';
    }
  }

  private applyPhoneValidationErrors(e: typeof this.fieldErrors): void {
    const contactPhoneErr = validateNationalPhone(this.contactCountryIso, this.contactNational);
    if (contactPhoneErr) {
      e.contactPhone = contactPhoneErr;
    }
    const personPhoneErr = validateNationalPhone(
      this.contactPersonCountryIso,
      this.contactPersonNational,
    );
    if (personPhoneErr) {
      e.contactPersonPhone = personPhoneErr;
    }
  }

  /**
   * Tax identifiers are country-specific: India requires GSTIN + PAN, UAE a TRN, and a
   * country with no rules requires none.
   */
  private applyTaxIdValidationErrors(e: typeof this.fieldErrors): void {
    if (this.isIndiaTenant) {
      this.applyIndianTaxIdValidationErrors(e);
      return;
    }
    if (this.isUaeTenant && !/^\d{15}$/.test(this.trn.replace(/\s/g, ''))) {
      e.trn = 'Enter a valid UAE TRN (15 digits).';
    }
  }

  private applyIndianTaxIdValidationErrors(e: typeof this.fieldErrors): void {
    if (!normalizeGstin(this.gstn).trim()) {
      e.gstn = 'GST number (GSTIN) is required.';
    }
    if (!normalizePan(this.businessPan).trim()) {
      e.businessPan = 'Business PAN is required.';
    }
    if (!this.profilePictureFile) {
      e.panImage = 'PAN card image is required.';
    }

    const gstErr = validateIndianGstin(this.gstn);
    if (gstErr) {
      e.gstn = gstErr;
    }
    const panErr = validateIndianPan(this.businessPan);
    if (panErr) {
      e.businessPan = panErr;
    }
  }

  private validateBillingFrequency(): boolean {
    if (this.isPostpaid && !this.billingFrequency) {
      this.submitError = 'Please select a billing frequency.';
      return false;
    }
    return true;
  }

  private applySubdomainValidationErrors(
    e: typeof this.fieldErrors,
    sub: string,
  ): void {
    if (!sub) {
      e.tenantSubdomain = 'Client subdomain is required.';
      return;
    }
    if (sub.length < 3 || sub.length > 63) {
      e.tenantSubdomain = 'Subdomain must be between 3 and 63 characters.';
      return;
    }
    if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(sub)) {
      e.tenantSubdomain =
        'Subdomain may only contain lowercase letters, numbers, and hyphens (cannot start/end with a hyphen).';
    }
  }

  private applyEmailValidationErrors(e: typeof this.fieldErrors, mail: string): void {
    if (!mail) {
      e.email = 'Email is required.';
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      e.email = 'Enter a valid email address.';
    }
  }

  private appendBillingAddress(fd: FormData): void {
    const fields: [string, string][] = [
      ['billing_address', this.billingAddress],
      ['billing_city', this.billingCity],
      ['billing_pincode', this.billingPincode],
    ];
    for (const [key, value] of fields) {
      if (value.trim()) fd.append(key, value.trim());
    }
  }

  onSubmit(): void {
    this.submitError = '';
    this.fieldErrors = {};
    this.contactPhoneField?.clearError();
    this.personPhoneField?.clearError();

    const e = this.collectFieldValidationErrors();
    this.fieldErrors = e;
    if (Object.keys(e).length > 0) {
      return;
    }
    if (!this.validateBillingFrequency()) {
      return;
    }

    const name = this.businessName.trim();
    const sub = this.tenantSubdomain.trim().toLowerCase();
    const mail = this.email.trim().toLowerCase();

    if (
      this.catalogLoading ||
      this.catalogLoadError ||
      this.productsCatalog.length === 0
    ) {
      this.submitError =
        this.catalogLoadError ||
        'Product type catalog is still loading or unavailable. Refresh and try again.';
      return;
    }

    const dateIso = this.receiptDateToIso(this.dateOfReceipt);

    const metadata = buildCommercialMetadataJson(
      this.productsCatalog,
      [],
      this.productSelection,
      {},
      this.productCharges,
    );

    const fd = new FormData();
    fd.append('tenant_name', name);
    fd.append('tenant_subdomain', sub);
    fd.append('email', mail);
    fd.append('submodule_ids', '[]');
    fd.append('currency', JSON.stringify({ code: this.currencyCode, symbol: this.currencySymbol }));
    fd.append('timezone', this.timezone);

    const mainDigits = normalizeNationalDigits(this.contactNational);
    fd.append('contact_number_country_code', mainDigits ? dialStringForCountry(this.contactCountryIso) : '');
    fd.append('contact_number', mainDigits);
    if (mainDigits) {
      fd.append('contact_number_country_iso', String(this.contactCountryIso).toUpperCase());
    }

    if (this.contactPerson.trim()) fd.append('contact_person', this.contactPerson.trim());

    const personDigits = normalizeNationalDigits(this.contactPersonNational);
    fd.append(
      'contact_person_phone_country_code',
      personDigits ? dialStringForCountry(this.contactPersonCountryIso) : '',
    );
    fd.append('contact_person_phone', personDigits);
    if (personDigits) {
      fd.append('contact_person_phone_country_iso', String(this.contactPersonCountryIso).toUpperCase());
    }
    fd.append('country_iso2', this.countryIso2);
    fd.append('tax_identifiers', JSON.stringify(this.taxIdentifiersPayload()));
    fd.append('gstn', normalizeGstin(this.gstn));
    fd.append('business_pan', normalizePan(this.businessPan));
    this.appendBillingAddress(fd);

    if (this.amountReceived.trim()) fd.append('commercial_amount_received', this.amountReceived.trim());
    if (dateIso) fd.append('commercial_date_of_receipt', dateIso);
    if (this.unNumber.trim()) fd.append('commercial_un_number', this.unNumber.trim());
    // Per-product per-PD charges travel inside commercial_metadata.productCharges.
    fd.append('commercial_metadata', metadata);
    fd.append('billing_type', this.billingType);
    if (this.isPostpaid) {
      fd.append('billing_frequency', this.billingFrequency);
    }

    if (this.logoFile) {
      fd.append('logo', this.logoFile, this.logoFile.name);
    }
    if (this.profilePictureFile) {
      fd.append('profile_picture', this.profilePictureFile, this.profilePictureFile.name);
    }

    this.submitting = true;
    this.tenantApi
      .createTenant(fd)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (res) => this.openSuccessModal(res?.data?.api_key ?? null),
        error: (err: unknown) => applyTenantFormApiErrorToForm(err, this, this.uiAlert),
      });
  }

  private openSuccessModal(apiKey: IssuedApiKey | null): void {
    this.clearSuccessRedirectTimer();
    this.issuedApiKey = apiKey;
    this.apiKeyCopied = false;
    this.showSuccessModal = true;
    // When a one-time API key is shown, let the operator copy it before leaving —
    // no auto-redirect. Otherwise keep the brief auto-redirect.
    if (!apiKey) {
      this.successRedirectTimer = setTimeout(() => {
        this.successRedirectTimer = null;
        this.navigateToUserList();
      }, 2500);
    }
  }

  copyApiKey(): void {
    if (!this.issuedApiKey) return;
    void navigator.clipboard.writeText(this.issuedApiKey.key).then(() => {
      this.apiKeyCopied = true;
    });
  }

  closeSuccessModal(): void {
    this.clearSuccessRedirectTimer();
    this.showSuccessModal = false;
    this.issuedApiKey = null;
    this.apiKeyCopied = false;
    this.navigateToUserList();
  }

  private navigateToUserList(): void {
    this.showSuccessModal = false;
    void this.router.navigate(['/manage-client']);
  }

  private clearSuccessRedirectTimer(): void {
    if (this.successRedirectTimer !== null) {
      clearTimeout(this.successRedirectTimer);
      this.successRedirectTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearSuccessRedirectTimer();
  }

  private hasAnyProductSelected(): boolean {
    return hasAnyProductSelected(this.productsCatalog, this.productSelection);
  }

  private receiptDateToIso(value: unknown): string {
    let d: Date | null = null;
    if (typeof value === 'string') {
      d = new Date(value);
    } else if (value instanceof Date) {
      d = value;
    }
    if (d && !Number.isNaN(d.getTime())) {
      return d.toISOString();
    }
    return '';
  }
}
